import { useEffect, useMemo, useState } from "react";

import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useSavedFilters } from "@vega-modules/hooks/useSavedFilters";

import {
  checkIfFilterValueChanged,
  getDefaultValue,
  sanitizeFilterState,
} from "../../hooks/useTableFilters";
import {
  type FilterState,
  type FilterType,
  type FilterValue,
  type TableFilterId,
} from "../../types";

export const SaveFiltersResults = ({
  filterState,
  onFilterChange,
  id,
  applyContextInPersistenceKeys = true,
  disabled = false,
}: {
  filterState: FilterState;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  id: TableFilterId;
  applyContextInPersistenceKeys?: boolean;
  disabled?: boolean;
}) => {
  const [savedFilterId, setSavedFilterId] = useState<string>("");

  const { savedFilters, loadFilter } = useSavedFilters(
    id,
    applyContextInPersistenceKeys,
  );

  const matchingFilterId = useMemo(() => {
    if (savedFilters.length === 0) {
      return "";
    }

    const sanitizedCurrentState = sanitizeFilterState(filterState);

    for (const savedFilter of savedFilters) {
      const savedFilterState = loadFilter(savedFilter.id);

      if (!savedFilterState) continue;

      const sanitizedSavedState = sanitizeFilterState(savedFilterState);
      const isFilterStateEqual =
        JSON.stringify(sanitizedSavedState) ===
        JSON.stringify(sanitizedCurrentState);

      if (isFilterStateEqual) {
        return savedFilter.id;
      }
    }

    return "";
  }, [savedFilters, filterState, loadFilter]);

  // Update state only when the matching filter ID changes
  useEffect(() => {
    if (matchingFilterId !== savedFilterId) {
      setSavedFilterId(matchingFilterId);
    }
  }, [matchingFilterId, savedFilterId]);

  const hasNoSavedFilters = savedFilters.length === 0;
  const options: OptionType[] = savedFilters
    .filter((filter) => isTruthy(filter.id) && isTruthy(filter.filterName))
    .map((filter) => ({
      label: filter.filterName,
      value: filter.id,
    }));

  const handleChange = (id: string) => {
    const filterState = loadFilter(id);

    if (isTruthy(filterState)) {
      try {
        Object.entries(filterState).forEach(([key, value]) => {
          const filterId = key;
          const typedValue = value as { value: FilterValue; type: FilterType };
          const filterValue = typedValue.value;
          const filterType = typedValue.type;

          if (
            filterValue &&
            checkIfFilterValueChanged(filterValue, filterType)
          ) {
            onFilterChange(filterId, filterValue);
          } else {
            onFilterChange(filterId, getDefaultValue(filterType));
          }
        });
      } catch (error) {
        console.error("Failed to parse saved filter config", error);
      }
    }
  };

  return (
    <div className="flex items-center gap-2 body-base-medium">
      <p className="text-text/secondary">{m.common_saved_filters()}:</p>
      {hasNoSavedFilters ? (
        <p className="text-text/primary">{m.common_no_saved_filters()}</p>
      ) : (
        <ThemedDropdown
          className="bg-background/secondary p-2 rounded-default"
          disabled={disabled}
          isClearable={false}
          name="saved-filter"
          onChange={(value) => value && handleChange(value.value)}
          options={options}
          placeholder={m.common_selectSavedFilter()}
          value={savedFilterId}
          variant="compact"
        />
      )}
    </div>
  );
};
